import { createContext, useContext, useEffect, useState } from 'react';
import type { Session, User } from '@supabase/supabase-js';
import { supabase } from '../lib/supabase';

const ADMIN_ROLES = new Set(['platform_admin', 'gym_admin', 'staff', 'admin']);

type AuthProfile = {
  id: string;
  email: string;
  name: string;
  role: string;
  gimnasioId: string | null;
  sucursalId: string | null;
};

type AuthContextType = {
  user: User | null;
  profile: AuthProfile | null;
  loading: boolean;
  signIn: (email: string, password: string) => Promise<void>;
  signOut: () => Promise<void>;
};

const AuthContext = createContext<AuthContextType | undefined>(undefined);

const normalizeRole = (value: string | null) => {
  if (!value) return null;
  const normalized = value.trim().toLowerCase();
  return normalized.length > 0 ? normalized : null;
};

const getMetaValue = (user: User, key: string): string | null => {
  const appMeta = user.app_metadata?.[key];
  const userMeta = user.user_metadata?.[key];
  const value = appMeta ?? userMeta;

  if (typeof value === 'string' && value.trim().length > 0) {
    return value;
  }

  return null;
};

const buildMetadataProfile = (user: User): AuthProfile => {
  const role = normalizeRole(getMetaValue(user, 'role')) || 'cliente';
  const name =
    getMetaValue(user, 'nombre') ||
    getMetaValue(user, 'name') ||
    user.email?.split('@')[0] ||
    'Administrador';

  return {
    id: user.id,
    email: user.email || '',
    name,
    role,
    gimnasioId: getMetaValue(user, 'gimnasio_id'),
    sucursalId: getMetaValue(user, 'sucursal_id'),
  };
};

const buildAdminProfile = async (user: User): Promise<AuthProfile | null> => {
  const { data, error } = await supabase
    .from('usuarios_admin')
    .select('id,email,nombre,role,gimnasio_id,sucursal_id,activo')
    .eq('auth_id', user.id)
    .maybeSingle();

  if (error) {
    console.error('Error cargando usuarios_admin:', error);
    return null;
  }

  if (!data || data.activo === false) {
    return null;
  }

  return {
    id: user.id,
    email: data.email || user.email || '',
    name: data.nombre || user.email?.split('@')[0] || 'Administrador',
    role: normalizeRole(data.role) || 'cliente',
    gimnasioId: data.gimnasio_id || null,
    sucursalId: data.sucursal_id || null,
  };
};

const resolveProfile = async (user: User): Promise<AuthProfile> => {
  const adminProfile = await buildAdminProfile(user);
  if (adminProfile) {
    return adminProfile;
  }

  return buildMetadataProfile(user);
};

const isAdminProfile = (profile: AuthProfile) => ADMIN_ROLES.has(profile.role);

const getCurrentRoutePath = () => {
  if (__ROUTER_MODE__ === 'hash') {
    const rawHash = window.location.hash.replace(/^#/, '');
    if (rawHash.startsWith('/')) {
      return rawHash.split('?')[0] || '/';
    }
  }

  return window.location.pathname;
};

const buildResetPasswordRedirectUrl = () => {
  if (__ROUTER_MODE__ === 'hash') {
    const rawHash = window.location.hash.replace(/^#/, '');
    const hashQuery = rawHash.startsWith('/') ? (rawHash.split('?')[1] || '') : rawHash;
    const params = new URLSearchParams(hashQuery);
    const qs = params.toString();
    const baseUrl = `${window.location.origin}${window.location.pathname}${window.location.search}`;
    return `${baseUrl}#/reset-password${qs ? `?${qs}` : ''}`;
  }

  const nextHash = window.location.hash || '';
  return `/reset-password${nextHash}`;
};

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [profile, setProfile] = useState<AuthProfile | null>(null);
  const [loading, setLoading] = useState(true);

  const applySession = async (session: Session | null) => {
    if (!session?.user) {
      setUser(null);
      setProfile(null);
      setLoading(false);
      return;
    }

    const nextProfile = await resolveProfile(session.user);

    if (!isAdminProfile(nextProfile)) {
      await supabase.auth.signOut();
      setUser(null);
      setProfile(null);
      setLoading(false);
      return;
    }

    setUser(session.user);
    setProfile(nextProfile);
    setLoading(false);
  };

  useEffect(() => {
    const rawHash = window.location.hash.replace(/^#/, '');
    const hashQuery = rawHash.startsWith('/') ? (rawHash.split('?')[1] || '') : rawHash;
    const hashParams = new URLSearchParams(hashQuery);
    const searchParams = new URLSearchParams(window.location.search);
    const isRecoveryLink = hashParams.get('type') === 'recovery' || searchParams.get('type') === 'recovery';
    const currentRoutePath = getCurrentRoutePath();

    if (isRecoveryLink && currentRoutePath !== '/reset-password') {
      window.location.replace(buildResetPasswordRedirectUrl());
      return;
    }

    supabase.auth.getSession().then(({ data }) => {
      applySession(data.session);
    });

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((event, session) => {
      if (event === 'PASSWORD_RECOVERY' && getCurrentRoutePath() !== '/reset-password') {
        window.location.replace(buildResetPasswordRedirectUrl());
        return;
      }
      applySession(session);
    });

    return () => {
      subscription.unsubscribe();
    };
  }, []);

  const signIn = async (email: string, password: string) => {
    const { data, error } = await supabase.auth.signInWithPassword({
      email,
      password,
    });

    if (error) {
      throw error;
    }

    if (!data.user) {
      throw new Error('No se pudo obtener el usuario autenticado');
    }

    const nextProfile = await resolveProfile(data.user);

    if (!isAdminProfile(nextProfile)) {
      await supabase.auth.signOut();
      throw new Error('Tu cuenta no tiene permisos para acceder al panel administrativo');
    }
  };

  const signOut = async () => {
    const { error } = await supabase.auth.signOut();
    if (error) {
      throw error;
    }
    setUser(null);
    setProfile(null);
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        profile,
        loading,
        signIn,
        signOut,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth debe usarse dentro de AuthProvider');
  }
  return context;
}
